import { getBasicExtensions } from "@nnr/tiptap-extensions";
import { Extension, useEditor } from "@tiptap/react";
import { useEffect, useState } from "react";
import { Markdown } from "tiptap-markdown";
import { Command } from "../Command/CommandExtension";
import {
  AuthenticatedImageNodeWithView,
  LibraryImageNodeWithView,
  MermaidCodeBlockNodeWithView,
  SectionTitleNodeWithView,
  YouTubeVideoNodeWithView,
} from "./nodes";

export function useBasicEditor(params?: UseBasicEditorOptions) {
  const [isWriting, setIsWriting] = useState(false);
  const writingTimeout = params?.writingDebounceMs ?? 1500;

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions: [
        ...getBasicExtensions({
          placeholder: params?.placeholder,
          history: true,
        }),
        MermaidCodeBlockNodeWithView,

        // Custom Nodes with React Views
        YouTubeVideoNodeWithView,
        SectionTitleNodeWithView,
        AuthenticatedImageNodeWithView,
        LibraryImageNodeWithView,

        Markdown.configure({
          transformCopiedText: true,
          transformPastedText: true,
        }),

        Command.configure({
          includeSkillMentions: params?.includeSkillMentions,
          excludeSkillUuid: params?.excludeSkillUuid,
        }),

        ...(params?.extensions ?? []),
      ],
      editorProps: {
        handlePaste: (_, event: ClipboardEvent) => {
          if (event.clipboardData?.types.includes("Files")) {
            return true;
          }
        },

        handleDrop: (_, event: DragEvent) => {
          if (event.dataTransfer?.types.includes("Files")) {
            return true;
          }
        },
      },
      autofocus: true,
      editable: true,
      injectCSS: false,
      content: params?.content ?? "",
      onUpdate: () => {
        setIsWriting(true);
        if (editor) {
          params?.onChange?.(editor.getHTML());
        }
      },
    },
    [
      ...(params?.deps ?? []),
      params?.includeSkillMentions,
      params?.excludeSkillUuid,
    ],
  );

  useEffect(() => {
    if (isWriting) {
      const timer = setTimeout(() => {
        setIsWriting(false);
        if (editor) {
          params?.onUpdate?.(editor.getHTML());
        }
      }, writingTimeout);

      return () => clearTimeout(timer);
    }
  }, [isWriting, writingTimeout, params, editor]);

  return {
    editor,
    isWriting,
  };
}

type UseBasicEditorOptions = {
  placeholder?: string;
  writingDebounceMs?: number;
  extensions?: Extension[];
  deps?: unknown[];
  content?: string;
  onUpdate?: (html: string) => void;
  onChange?: (html: string) => void;
  includeSkillMentions?: boolean;
  excludeSkillUuid?: string;
};
